"use client";

import { plural } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Alert } from "@offstreetparking/design-system/feedback";
import { formatDate } from "@offstreetparking/design-system/formatters";
import { Button } from "@offstreetparking/design-system/forms";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { ButtonRow } from "@offstreetparking/portal-flow/components/layouts/ButtonRow";
import type { RouterOutputs } from "~/services/offstreetApi/types";

type CodeMetrics = RouterOutputs["registrationCode"]["checkCodeMetricsForSetting"];

type CodeMetricsDisplayProps = {
	metrics: CodeMetrics;
	onCheckAnother: () => void;
};
export const CodeMetricsDisplay = ({ metrics, onCheckAnother }: CodeMetricsDisplayProps) => {
	const { t } = useLingui();
	const isUnlimited = metrics.remaining === null;
	const remainingDisplay = (() => {
		if (metrics.remaining === 0) {
			return t({
				message: "Exhausted",
				comment: "Shown instead of a number when a code has no remaining uses. Not 'tired' and not 'sold out'.",
			});
		}
		return String(metrics.remaining);
	})();
	const resetDateDisplay = (() => {
		if (metrics.inNumberOfDays >= 1) {
			return null;
		}
		if (!metrics.windowEnd || metrics.remaining === null) {
			return null;
		}
		return formatDate({
			date: metrics.windowEnd,
			formatType: "Abbreviated_Date",
		});
	})();
	const hoursRemainingDisplay = (() => {
		const ctx = metrics.maxTimeUsageContext;
		if (metrics.maxHours === undefined || metrics.hours === undefined || !ctx) {
			return null;
		}
		const remaining = Math.max(0, metrics.maxHours - metrics.hours).toFixed(2);
		const period = (() => {
			if (ctx.type === "days") {
				return t`${plural(ctx.days ?? 1, {
					one: "Per Day",
					other: "Per # Days",
				})}`;
			}
			if (ctx.type === "week") {
				return t`Per Week`;
			}
			if (ctx.type === "month") {
				return t`Per Month`;
			}
			return null;
		})();
		return { remaining, max: metrics.maxHours, period };
	})();
	const showUsageStats = !isUnlimited;
	const showStatsBlock = showUsageStats || !!hoursRemainingDisplay;
	return (
		<div className="space-y-6 p-8">
			<Heading>{t`Code Results`}</Heading>

			<Alert
				type={metrics.valid ? "success" : "error"}
				title={metrics.valid ? t`This code is valid.` : t`This code is not valid.`}
			/>

			{metrics.valid && showStatsBlock && (
				<div className="space-y-3">
					{showUsageStats && (
						<>
							<div className="flex justify-between">
								<Text size="sm" intent="muted">{t`Remaining Uses`}</Text>
								<Text size="sm" weight="bold">
									{remainingDisplay}
								</Text>
							</div>

							<div className="flex justify-between">
								<Text size="sm" intent="muted">{t`Lifetime Uses`}</Text>
								<Text size="sm" weight="bold">
									{metrics.lifetimeUses}
								</Text>
							</div>
						</>
					)}

					{hoursRemainingDisplay && (
						<div className="flex justify-between">
							<Text size="sm" intent="muted">{t`Hours Remaining`}</Text>
							<Text size="sm" weight="bold">
								{hoursRemainingDisplay.period
									? `${hoursRemainingDisplay.remaining} / ${hoursRemainingDisplay.max} (${hoursRemainingDisplay.period})`
									: `${hoursRemainingDisplay.remaining} / ${hoursRemainingDisplay.max}`}
							</Text>
						</div>
					)}

					{showUsageStats && metrics.inNumberOfDays >= 1 && (
						<Text size="sm" intent="muted">
							{t`${plural(metrics.inNumberOfDays, {
								one: "Remaining uses reset on a rolling window of # day",
								other: "Remaining uses reset on a rolling window of # days",
							})}`}
						</Text>
					)}
					{showUsageStats && resetDateDisplay && (
						<Text size="sm" intent="muted">
							{t`Remaining uses reset to ${String(metrics.amount)} after ${resetDateDisplay}`}
						</Text>
					)}
				</div>
			)}

			<ButtonRow>
				<Button className="w-full whitespace-nowrap text-base md:w-auto" type="button" onClick={onCheckAnother}>
					{t`Check Another Code`}
				</Button>
			</ButtonRow>
		</div>
	);
};
